import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { DollarSign, TrendingUp, Clock, CheckCircle } from "lucide-react";
import api from "../lib/api";

const CHANNEL_COLORS = {
  Retail: "hsl(12, 76%, 61%)",
  GDO: "hsl(173, 58%, 39%)",
  DO: "hsl(197, 37%, 24%)",
  "Ho.Re.Ca.": "hsl(43, 74%, 66%)",
};

export default function CommissionsPage() {
  const { isAdmin } = useAuth();
  const [commissions, setCommissions] = useState(null);
  const [agentCommissions, setAgentCommissions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCommissions();
  }, [isAdmin]);

  const fetchCommissions = async () => {
    try {
      const response = await api.get("/analytics/commissions");
      setCommissions(response.data);

      if (isAdmin) {
        const agentRes = await api.get("/analytics/revenue-by-agent");
        setAgentCommissions(agentRes.data);
      }
    } catch (error) {
      console.error("Error fetching commissions:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value);
  };

  const formatPercent = (value) => {
    return `${(value * 100).toFixed(0)}%`;
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-muted rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="h-80 bg-muted rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const channelCommData = Object.entries(
    commissions?.commissions_by_channel || {}
  ).map(([name, value]) => ({
    name,
    value,
    color: CHANNEL_COLORS[name] || "hsl(200, 50%, 50%)",
  }));

  const ratesData = Object.entries(commissions?.commission_rates || {}).map(
    ([channel, rate]) => ({
      channel,
      rate: rate * 100,
      color: CHANNEL_COLORS[channel] || "hsl(200, 50%, 50%)",
    })
  );

  const agentData = Object.entries(agentCommissions?.commissions_by_agent || {}).map(
    ([name, value]) => ({
      name,
      commission: value,
      revenue: agentCommissions?.revenue_by_agent?.[name] || 0,
    })
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="commissions-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-primary font-bold">Provvigioni</h1>
        <p className="text-muted-foreground">
          Monitora le provvigioni maturate e da incassare
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Provvigioni Totali
                </p>
                <p className="stat-card-value text-green-600" data-testid="total-commissions">
                  {formatCurrency(commissions?.total_commissions || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Da ordini consegnati
                </p>
              </div>
              <div className="stat-card-icon bg-green-500/10">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Provvigioni in Corso
                </p>
                <p className="stat-card-value text-amber-600" data-testid="pending-commissions">
                  {formatCurrency(commissions?.pending_commissions || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Da ordini in elaborazione
                </p>
              </div>
              <div className="stat-card-icon bg-amber-500/10">
                <Clock className="w-6 h-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card sm:col-span-2 lg:col-span-1">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Totale Provvigioni
                </p>
                <p className="stat-card-value text-accent" data-testid="all-commissions">
                  {formatCurrency(
                    (commissions?.total_commissions || 0) +
                      (commissions?.pending_commissions || 0)
                  )}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Maturate + In corso
                </p>
              </div>
              <div className="stat-card-icon bg-accent/10">
                <DollarSign className="w-6 h-6 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts and Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Commission Rates */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Aliquote Provvigioni</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {ratesData.map((item) => (
                <div key={item.channel} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{item.channel}</span>
                    <span className="text-sm font-mono font-bold">
                      {item.rate}%
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${item.rate * 10}%`,
                        backgroundColor: item.color,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Commissions by Channel - Pie */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Provvigioni per Canale</CardTitle>
          </CardHeader>
          <CardContent>
            {channelCommData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={channelCommData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {channelCommData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                Nessun dato disponibile
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Agent Commissions - Admin Only */}
      {isAdmin && agentData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Provvigioni per Agente</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agente</TableHead>
                    <TableHead className="text-right">Fatturato</TableHead>
                    <TableHead className="text-right">Provvigioni</TableHead>
                    <TableHead className="text-right">%</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agentData.map((agent) => (
                    <TableRow key={agent.name}>
                      <TableCell className="font-medium">{agent.name}</TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(agent.revenue)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-green-600">
                        {formatCurrency(agent.commission)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">
                        {agent.revenue > 0
                          ? ((agent.commission / agent.revenue) * 100).toFixed(1)
                          : 0}
                        %
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Commission Bar Chart */}
      {isAdmin && agentData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Confronto Performance Agenti</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={agentData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  tickFormatter={(v) => `€${v / 1000}k`}
                />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{
                    backgroundColor: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px",
                  }}
                />
                <Bar
                  dataKey="revenue"
                  fill="hsl(12, 76%, 61%)"
                  name="Fatturato"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  dataKey="commission"
                  fill="hsl(173, 58%, 39%)"
                  name="Provvigioni"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
